import { useQueryClient } from '@tanstack/react-query';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { noop, uniq, without, xor } from 'lodash-es';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ExpandedState } from '~components/ExpandedStateButton/ExpandedStateButton';
import { Invoice } from '~hooks/useInvoices/models';
import { Order } from '~hooks/useOrders/models';
import useOrderCardsExpandedState, {
  DEFAULT_ORDER_CARDS_EXPANDED_STATE,
  OrderCardsExpandedState,
} from '~pages/Approvals/shared/useOrderCardsExpandedState';

import { TabQSParam } from '../DriverPay/TabQSParam';
import { hasActionsForInvoice, makeInvoicesQueryContextKey } from './utils';

type CheckedState = boolean | 'intermediate';

type DriverPayPageContextType = {
  orderCardsExpandedState: OrderCardsExpandedState;
  toggleAllOrderCardsExpandedState: (expandedState: `${ExpandedState}`) => void;
  toggleSingleOrderCardsExpandedState: (
    orderId: string,
    expandedState: `${ExpandedState}`,
  ) => void;
  currentCategory: InvoiceCategoryFilter;
  setCurrentCategory: (category: InvoiceCategoryFilter) => void;
  checkedInvoices: Invoice[];
  checkedInvoiceIds: string[];
  getInvoiceCheckedState: (invoice: Invoice) => boolean;
  toggleInvoiceCheck: (invoice: Invoice) => void;
  uncheckAllInvoices: () => void;
  getOrderCheckedState: (order: Order) => CheckedState;
  toggleOrderCheck: (order: Order) => void;
};

const LOCAL_STORAGE_KEY_EXPANDED_STATE = 'driverPay_expandedState';

const DriverPayPageContext = createContext<DriverPayPageContextType>({
  orderCardsExpandedState: DEFAULT_ORDER_CARDS_EXPANDED_STATE,
  toggleAllOrderCardsExpandedState: noop,
  toggleSingleOrderCardsExpandedState: noop,
  currentCategory: InvoiceCategoryFilter.PAYABLES, // Default to PAYABLES
  setCurrentCategory: noop,
  checkedInvoices: [],
  checkedInvoiceIds: [],
  getInvoiceCheckedState: () => false,
  toggleInvoiceCheck: noop,
  uncheckAllInvoices: noop,
  getOrderCheckedState: () => false,
  toggleOrderCheck: noop,
});

function DriverPayPageContextProvider({ children }: React.PropsWithChildren) {
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    orderCardsExpandedState,
    toggleAllOrderCardsExpandedState,
    toggleSingleOrderCardsExpandedState,
  } = useOrderCardsExpandedState(LOCAL_STORAGE_KEY_EXPANDED_STATE);

  const [currentCategory, setCurrentCategory] = useState<InvoiceCategoryFilter>(() => {
    const categoryParam = searchParams.get('category');
    return (categoryParam as InvoiceCategoryFilter) || InvoiceCategoryFilter.PAYABLES;
  });

  const [checkedInvoices, setCheckedInvoices] = useState<Invoice[]>([]);
  // Sync category changes to URL
  const updateCategory = useCallback(
    (category: InvoiceCategoryFilter) => {
      setCurrentCategory(category);
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('category', category);
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Sync URL changes to state
  useEffect(() => {
    const categoryParam = searchParams.get('category') as InvoiceCategoryFilter;
    if (categoryParam && categoryParam !== currentCategory) {
      setCurrentCategory(categoryParam);
    }
  }, [searchParams, currentCategory]);

  const toggleInvoiceCheck = useCallback<DriverPayPageContextType['toggleInvoiceCheck']>(
    (invoice) => {
      setCheckedInvoices((state) => xor(state, [invoice]));
    },
    [],
  );

  const getInvoiceCheckedState = useCallback<
    DriverPayPageContextType['getInvoiceCheckedState']
  >(
    (invoice) => {
      return checkedInvoices.includes(invoice);
    },
    [checkedInvoices],
  );

  const uncheckAllInvoices = useCallback<DriverPayPageContextType['uncheckAllInvoices']>(
    () => setCheckedInvoices([]),
    [],
  );

  const checkedInvoiceIds = checkedInvoices.map((invoice) => invoice.id ?? '');

  const getInvoicesInOrder = useCallback(
    (order: Order) => {
      const invoicesQueryKey = makeInvoicesQueryContextKey(order.id, currentCategory);

      return queryClient.getQueryData<Invoice[]>([invoicesQueryKey]) ?? [];
    },
    [currentCategory],
  );

  const getOrderCheckedState = useCallback<
    DriverPayPageContextType['getOrderCheckedState']
  >(
    (order) => {
      const invoicesInOrder = getInvoicesInOrder(order).map((invoice) => invoice.id);
      const checkedInvoicesInOrder = checkedInvoiceIds.filter((invoiceId) => {
        return invoicesInOrder.includes(invoiceId);
      });

      const noInvoicesChecked = checkedInvoicesInOrder.length === 0;

      if (noInvoicesChecked) {
        return false;
      }

      const allInvoicesChecked = checkedInvoicesInOrder.length === invoicesInOrder.length;

      if (allInvoicesChecked) {
        return true;
      }

      return 'intermediate';
    },
    [getInvoicesInOrder, checkedInvoiceIds],
  );

  const toggleOrderCheck = useCallback<DriverPayPageContextType['toggleOrderCheck']>(
    (order) => {
      setCheckedInvoices((state) => {
        const invoicesInOrder = getInvoicesInOrder(order).filter((invoice) =>
          hasActionsForInvoice(invoice, searchParams.get('tab') as TabQSParam),
        );
        const checkedState = getOrderCheckedState(order);
        const shouldCheckAllInvoices = checkedState === false;

        if (shouldCheckAllInvoices) {
          return uniq([...state, ...invoicesInOrder]);
        }

        return without(state, ...invoicesInOrder);
      });
    },
    [getInvoicesInOrder, getOrderCheckedState],
  );

  return (
    <DriverPayPageContext.Provider
      value={{
        orderCardsExpandedState,
        toggleAllOrderCardsExpandedState,
        toggleSingleOrderCardsExpandedState,
        currentCategory,
        setCurrentCategory: updateCategory,
        checkedInvoiceIds,
        getInvoiceCheckedState,
        toggleInvoiceCheck,
        uncheckAllInvoices,
        checkedInvoices,
        getOrderCheckedState,
        toggleOrderCheck,
      }}
    >
      {children}
    </DriverPayPageContext.Provider>
  );
}

export default DriverPayPageContext;
export { DriverPayPageContextProvider };
